import React from "react";
import { useState, useEffect, useCallback } from "react";
import { useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { API_BASE_URL, API_URL, NODE_API_BASE_URL } from "../../config";

import { placeClickToCall } from "../utils/clickToCall";
import axios from "axios";
import { toast } from "react-toastify";
import { Modal, Button, Input, Tooltip, Dropdown } from "antd";
import {
  MailOutlined,
  WhatsAppOutlined,
  MessageOutlined,
  CheckOutlined,
  CopyOutlined,
  FilterOutlined,
  ClearOutlined,
  SearchOutlined,
  CalendarOutlined,
  ShoppingOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  LinkOutlined,
  DownloadOutlined,
  FileProtectOutlined,
  UnorderedListOutlined,
  AppstoreOutlined,
  PhoneOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import TableLoader from "../../Loader/TableLoader";
import moment from "moment";
import { deleteOrder } from "../services/orderService";
import usePermissions from "../hooks/usePermissions";
import SubscriptionKanban, {
  COLUMNS,
  classifyOrder,
} from "./createCustomerOrder/SubscriptionKanban";
import { useQuery } from "@tanstack/react-query";
import { CommentOutlined } from "@ant-design/icons";
import { useAddCustomerFollowUp } from "../hooks/useCustomerFollowUp";
import { useFollowUpOptions } from "../hooks/useManageEnquiry";
import { authHeaders, getSessionToken } from "../utils/session";

const LIMIT = 10;

/* Each Kanban column pages on its own, so this is a screenful at a time
   rather than everything at once. Small on purpose: a board that asked for all
   ten thousand records up front spent its first seconds fetching rows nobody
   had scrolled to. */
const KANBAN_PAGE = 20;

/* What a column looks like before anything has been asked for. */
const EMPTY_COLUMN = { items: [], page: 0, total: 0, hasMore: true, loading: false };


// ── Follow-up status custom select (color-coded) ────────────────────────────
const statusColorME = (s) => {
  const m = {
    "No Response": "#9ca3af",
    Interested: "#16a34a",
    "Not Interested": "#6b7280",
    "Meeting Scheduled": "#0284c7",
    Converted: "#15803d",
    Closed: "#6b7280",
    "Follow Up": "#667eea",
    "Not Responding": "#9ca3af",
    "Hot Lead": "#ef4444",
    "Mark for Future": "#f59e0b",
    Negative: "#dc2626",
  };
  return m[s] || "#667eea";
};

const FollowUpStatusSelectME = ({
  value,
  onChange,
  followUpOptionsList = [],
}) => {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef();
  const selected = followUpOptionsList.find((o) => o.option_value === value);
  const color = statusColorME(selected?.label || "");
  React.useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        style={{
          width: "100%",
          padding: "9px 36px 9px 12px",
          border: `1.5px solid ${value ? color : "#fde68a"}`,
          borderRadius: "9px",
          fontSize: "13px",
          fontWeight: 700,
          color: value ? color : "#6b7280",
          background: value ? `${color}10` : "white",
          cursor: "pointer",
          textAlign: "left",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          boxSizing: "border-box",
          position: "relative",
        }}
      >
        <span style={{ flex: 1 }}>
          {selected?.label || "— Select Status —"}
        </span>
        <span
          style={{
            position: "absolute",
            right: "11px",
            top: "50%",
            transform: `translateY(-50%) rotate(${open ? "180deg" : "0deg"})`,
            transition: "transform 0.2s",
            color: value ? color : "#9ca3af",
            pointerEvents: "none",
            fontSize: "10px",
          }}
        >
          ▼
        </span>
      </button>
      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            right: 0,
            zIndex: 9999,
            background: "white",
            border: "1.5px solid #e8ecff",
            borderRadius: "12px",
            boxShadow: "0 8px 32px rgba(102,126,234,0.18)",
            overflow: "hidden",
          }}
        >
          <div
            style={{ maxHeight: "220px", overflowY: "auto", padding: "6px" }}
          >
            {followUpOptionsList.map((opt) => {
              const c = statusColorME(opt.label);
              const isActive = opt.option_value === value;
              return (
                <button
                  key={opt.option_value}
                  type="button"
                  onClick={() => {
                    onChange(opt.option_value);
                    setOpen(false);
                  }}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    border: "none",
                    borderRadius: "8px",
                    fontSize: "13px",
                    fontWeight: isActive ? 700 : 500,
                    color: isActive ? "white" : c,
                    background: isActive ? c : "transparent",
                    cursor: "pointer",
                    textAlign: "left",
                    display: "flex",
                    alignItems: "center",
                    gap: "9px",
                    transition: "all 0.15s",
                    marginBottom: "2px",
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) e.currentTarget.style.background = `${c}15`;
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive)
                      e.currentTarget.style.background = "transparent";
                  }}
                >
                  {opt.label}
                  {isActive && (
                    <span style={{ marginLeft: "auto", fontSize: "12px" }}>
                      ✓
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Custom DatePicker Input ────────────────────────────────────────────────
const CustomDateInput = React.forwardRef(
  ({ value, onClick, placeholder }, ref) => (
    <div
      onClick={onClick}
      ref={ref}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        padding: "8px 12px",
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        cursor: "pointer",
        minHeight: "42px",
        background: "white",
        transition: "all 0.2s",
        width: "100%",
        fontSize: "14px",
        color: value ? "#262626" : "#bfbfbf",
      }}
    >
      <CalendarOutlined
        style={{ color: "#667eea", fontSize: "14px", flexShrink: 0 }}
      />
      <span style={{ flex: 1 }}>{value || placeholder}</span>
    </div>
  ),
);

const ManageOrderList = ({ defaultStatusFilter = "" }) => {
  const { canCreate, canEdit, canDelete } = usePermissions(
    "/cms/manage-customer/manage-order",
  );
  const { search, state: locationState } = useLocation();
  const navigate = useNavigate();
  const filteredUserId = new URLSearchParams(search).get("userId");
  const filteredUserIdRef = React.useRef(filteredUserId);
  const filteredUserName = new URLSearchParams(search).get("userName");

  const [listPageData, setListPageData] = useState([]);
  const [token, setToken] = useState(getSessionToken());
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [pageApi, setPageApi] = useState(1);
  const [totalUser, setTotalUser] = useState(0);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [searchName, setSearchName] = useState("");
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState(searchName);
  const initialStatusFilter = locationState?.statusFilter || defaultStatusFilter;
  const [paymentStatusFilter, setPaymentStatusFilter] = useState(initialStatusFilter);
  const [planIdFilter, setPlanIdFilter] = useState("");

  // ── View mode + kanban filter ──
  const [viewMode, setViewMode] = useState(initialStatusFilter ? "table" : "kanban");
  const [kanbanFilter, setKanbanFilter] = useState(null);
  /* One entry per column, each paging on its own. */
  const [kanbanCols, setKanbanCols] = useState({});  /* The columns as they actually stand, which is what the loader decides from.
     A state updater cannot be used for that: React defers it, so a flag set
     inside one and read on the next line is still false. Worse, React runs the
     first updater eagerly when its queue is empty — so exactly one column
     fetched and the rest were left marked "loading" with no request ever sent.
     The ref is the truth; state is the copy that renders. */
  const kanbanColsRef = useRef({});
  /* Bumped every time a column is restarted, so a reply from the previous filter
     can be recognised and dropped instead of overwriting the new one. */
  const kanbanGenRef = useRef({});
  const writeColumn = useCallback((key, next) => {
    kanbanColsRef.current = { ...kanbanColsRef.current, [key]: next };
    setKanbanCols(kanbanColsRef.current);
  }, []);
  const [exportMode, setExportMode] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  // ── Call log modal state ──
  const [callLogModal, setCallLogModal] = useState(null);
  const [callOutcome, setCallOutcome] = useState("Connected");
  const [callNote, setCallNote] = useState("");
  const [callDurationME, setCallDurationME] = useState("");
  const [callFuStatusME, setCallFuStatusME] = useState("");
  const [callNextDateME, setCallNextDateME] = useState("");
  const [callNextTimeME, setCallNextTimeME] = useState("");

  const addCustomerFollowUp = useAddCustomerFollowUp();
  const { data: followUpOptions = [] } = useFollowUpOptions();

  const resetCallLog = () => {
    setCallLogModal(null);
    setCallNote("");
    setCallOutcome("Connected");
    setCallDurationME("");
    setCallFuStatusME("");
    setCallNextDateME("");
    setCallNextTimeME("");
  };

  const handleClickToCall = (clientNo, clientName) => {
    // agent_no = the logged-in user's own mobile; the helper also
    // reports an unregistered number instead of failing silently.
    placeClickToCall(clientNo, clientName);
  };

  const handleLogCallSubmit = () => {
    addCustomerFollowUp.mutate(
      {
        customer_id: callLogModal.id,
        followup_type: "call",
        followup_date: moment().format("YYYY-MM-DD HH:mm:ss"),
        enquiry_status: callFuStatusME,
        direction: "outbound",
        delivery_status: "delivered",
        call_outcome: callOutcome,
        call_duration: callDurationME || null,
        content: callNote || `Call: ${callOutcome}`,
        remarks: callNote || null,
        subject: `Call — ${callOutcome}`,
        recipient: callLogModal.phone,
        created_by: Number(localStorage.getItem("trade_user_id")) || 1,
        ...(callNextDateME
          ? {
              next_followup_date: callNextDateME,
              ...(callNextTimeME ? { next_followup_time: callNextTimeME } : {}),
            }
          : {}),
      },
      { onSuccess: resetCallLog },
    );
  };

  // ── Fetch all plans once (cached — plans rarely change) ──
  const { data: allPlansData } = useQuery({
    queryKey: ["allPlans"],
    queryFn: () =>
      axios
        .get(`${API_BASE_URL}/get-all-plans-v1`, {
          headers: { ...authHeaders() },
        })
        .then((res) => res.data?.data || []),
    staleTime: 30 * 60 * 1000, // 30 minutes
    cacheTime: 60 * 60 * 1000, // 1 hour
  });

  // Build a map of plan_id → plan_name for quick lookup
  const planMap = React.useMemo(() => {
    if (!allPlansData) return {};
    return allPlansData.reduce((acc, plan) => {
      acc[plan.id] = plan.name;
      return acc;
    }, {});
  }, [allPlansData]);

  const debounceTimerRef = useRef(null);
  const cancelRequestRef = useRef(null);

  // ── Scroll refs ──
  const scrollContainerRef = useRef(null);
  const throttleRef = useRef(false);
  const pageRef = useRef(1);

  useEffect(() => {
    filteredUserIdRef.current = filteredUserId;
  }, [filteredUserId]);

  useEffect(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchName);
      if (viewMode === "table") {
        resetAndFetchTable();
      }
    }, 500);
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [searchName]);

  // ── Auto-switch to table when a status filter is applied in kanban ──
  useEffect(() => {
    if (paymentStatusFilter && viewMode === "kanban") {
      setViewMode("table");
    }
  }, [paymentStatusFilter]); // eslint-disable-line

  // ── Fetch when dependencies change ──
  useEffect(() => {
    setToken(getSessionToken());
    if (viewMode === "kanban") {
      fetchKanban();
    }
  }, [
    viewMode,
    filteredUserId,
    debouncedSearch,
    paymentStatusFilter,
    planIdFilter,
    startDate,
    endDate,
  ]);

  // ── Re-fetch table when filters/search change or view switches to table ──
  useEffect(() => {
    if (viewMode === "table") {
      resetAndFetchTable();
    }
  }, [
    viewMode,
    debouncedSearch,
    startDate,
    endDate,
    paymentStatusFilter,
    planIdFilter,
  ]);

  // ── Payment modal ──
  const openPaymentModal = (order) => {
    setSelectedOrder(order);
    setShowPaymentModal(true);
  };

  const closePaymentModal = () => {
    setShowPaymentModal(false);
    setSelectedOrder(null);
  };

  const copyPaymentLink = () => {
    if (!selectedOrder?.payment_link) return;
    navigator.clipboard.writeText(selectedOrder.payment_link);
    setLinkCopied(true);
    toast.success("Payment link copied to clipboard");
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const handleSearch = () => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    if (cancelRequestRef.current)
      cancelRequestRef.current.cancel("Manual search");
    setDebouncedSearch(searchName);
    if (viewMode === "kanban") {
      fetchKanban();
    } else {
      resetAndFetchTable();
    }
  };

  const handleClearFilters = () => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    if (cancelRequestRef.current)
      cancelRequestRef.current.cancel("Clear clicked");
    setSearchName("");
    setDebouncedSearch("");
    setStartDate(null);
    setEndDate(null);
    setPaymentStatusFilter(defaultStatusFilter);
    setPlanIdFilter("");
    setKanbanFilter(null);
    if (viewMode === "kanban") {
      fetchKanban("");
    } else {
      resetAndFetchTable();
    }
  };

  const isFilterApplied = !!(searchName || startDate || endDate || paymentStatusFilter || planIdFilter);

  const formatDate = (date) => {
    if (!date) return undefined;
    const d = new Date(date);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };

  // ── Build customer filter params for API calls ──
  const getPaymentFilterParams = () => {
    if (paymentStatusFilter === "trial") return { customer_type: "trial" };
    if (paymentStatusFilter === "paid_active") return { customer_status: "paid_active" };
    if (paymentStatusFilter === "payment_pending") return { customer_status: "payment_pending" };
    if (paymentStatusFilter === "paid_expired") return { customer_status: "paid_expired" };
    if (paymentStatusFilter === "expiring_soon") return { customer_status: "expiring_soon" };
    if (paymentStatusFilter === "free_trial_expired") return { customer_status: "free_trial_expired" };
    return {};
  };

  // ══════════════════════════════════════════════════════════════════
  // KANBAN FETCH — one API call per column in parallel
  // ══════════════════════════════════════════════════════════════════
  /* Read through a ref so the loader can stay a stable callback: it is handed
     to an observer per column, and rebuilding it on every keystroke would tear
     those observers down and set them up again mid-scroll. */
  const kanbanBaseParamsRef = useRef(() => ({}));

  const KANBAN_COLUMNS_PARAMS = [
    { key: "paid",               params: { customer_status: "paid_active" } },
    { key: "free_trial",         params: { customer_type: "trial" } },
    { key: "pending",            params: { customer_status: "payment_pending" } },
    { key: "expiring_soon",      params: { customer_status: "expiring_soon" } },
    { key: "expired",            params: { customer_status: "paid_expired" } },
    { key: "free_trial_expired", params: { customer_status: "free_trial_expired" } },
    { key: "payment_rejected",   params: { customer_status: "payment_rejected" } },
  ];

  /* The filters every column asks with. Identical across columns on purpose —
     only the status params below differ, so a search narrows all seven at once
     and each column's count comes back filtered too. */
  const kanbanBaseParams = (searchOverride) => ({
    search: (searchOverride !== undefined ? searchOverride : debouncedSearch) || undefined,
    from_date: formatDate(startDate),
    to_date: formatDate(endDate),
    ...(planIdFilter ? { plan_id: planIdFilter } : {}),
  });
  kanbanBaseParamsRef.current = kanbanBaseParams;

  /**
   * Load one column's next page.
   *
   * `reset` starts it over, which is what a changed filter needs — otherwise
   * page 2 of the old search would be appended under page 1 of the new one. It
   * goes ahead even while a request is in flight, because the filter has
   * already changed and whatever that request returns is now the wrong answer;
   * the generation counter below is what stops it landing.
   *
   * Everything is decided from the ref, never from state — React defers a
   * state updater, so a flag set inside one is still unset on the next line.
   */
  const loadKanbanColumn = useCallback((key, { reset = false, searchOverride } = {}) => {
    const col = KANBAN_COLUMNS_PARAMS.find((c) => c.key === key);
    if (!col) return;

    const cur = kanbanColsRef.current[key] || EMPTY_COLUMN;
    // A scroll must not queue a second page on top of one already coming.
    if (!reset && (cur.loading || !cur.hasMore)) return;

    const gen = (kanbanGenRef.current[key] || 0) + 1;
    kanbanGenRef.current[key] = gen;

    const nextPage = reset ? 1 : cur.page + 1;
    writeColumn(key, reset ? { ...EMPTY_COLUMN, loading: true } : { ...cur, loading: true });

    axios
      .get(`${NODE_API_BASE_URL}/api/subscription/get-customer-list-v3`, {
        params: {
          ...kanbanBaseParamsRef.current(searchOverride),
          ...col.params,
          page: nextPage,
          limit: KANBAN_PAGE,
        },
        headers: { ...authHeaders() },
      })
      .then((res) => {
        const rows = (res.data?.data || []).map((item) => ({ ...item, _col: key }));
        /* A reply to a filter the user has already moved on from. Dropping
           it here is what lets a reset interrupt a request in flight. */
        if (kanbanGenRef.current[key] !== gen) return;
        const lastPage = res.data?.last_page || 1;
        {
          const held = kanbanColsRef.current[key] || EMPTY_COLUMN;
          writeColumn(key, {
              items: nextPage === 1 ? rows : [...held.items, ...rows],
              page: nextPage,
              total: res.data?.total ?? 0,
              /* Both conditions, not just the page number: a column whose last
                 page came back short still has nothing left to ask for. */
              hasMore: nextPage < lastPage && rows.length > 0,
              loading: false,
          });
        }
      })
      .catch(() => {
        if (kanbanGenRef.current[key] !== gen) return;
        /* Stop asking rather than retrying forever — the observer would fire
           again the moment the foot stayed in view. */
        writeColumn(key, {
          ...(kanbanColsRef.current[key] || EMPTY_COLUMN),
          loading: false,
          hasMore: false,
        });
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* Every column back to page one. This is what a changed search or filter
     calls, and what entering the board calls. */
  const fetchKanban = useCallback((searchOverride) => {
    KANBAN_COLUMNS_PARAMS.forEach(({ key }) =>
      loadKanbanColumn(key, { reset: true, searchOverride }));
  }, [loadKanbanColumn]);



  // ══════════════════════════════════════════════════════════════════
  // TABLE FETCH — paginated with infinite scroll
  // ══════════════════════════════════════════════════════════════════
  const fetchTablePage = async (page, isReset = false) => {
    if (isReset) setIsLoading(true);
    else setIsLoadingMore(true);

    const currentFilteredUserId = filteredUserIdRef.current;

    try {
      if (currentFilteredUserId) {
        const response = await axios.get(
          `${NODE_API_BASE_URL}/api/subscription/get-customer-list-v3`,
          {
            params: {
              page: 1,
              limit: 1000,
              search: debouncedSearch || undefined,
              from_date: formatDate(startDate),
              to_date: formatDate(endDate),
              ...getPaymentFilterParams(),
            },
            headers: { ...authHeaders() },
          },
        );
        const { data: orders } = response.data;
        const filtered = (orders || []).filter(
          (o) => String(o.user_id) === String(currentFilteredUserId),
        );
        setListPageData(filtered);
        setTotalUser(filtered.length);
        setHasMore(false);
        return;
      }

      const response = await axios.get(
        `${NODE_API_BASE_URL}/api/subscription/get-customer-list-v3`,
        {
          params: {
            page,
            limit: LIMIT,
            search: debouncedSearch || undefined,
            from_date: formatDate(startDate),
            to_date: formatDate(endDate),
            ...(planIdFilter ? { plan_id: planIdFilter } : {}),
            ...getPaymentFilterParams(),
          },
          headers: { ...authHeaders() },
        },
      );

      const { data: newData, total, last_page: lastPage } = response.data;

      setTotalUser(total);

      if (isReset) {
        setListPageData(newData || []);
      } else {
        setListPageData((prev) => [...prev, ...(newData || [])]);
      }

      setHasMore(page < lastPage);
    } catch (error) {
      if (!axios.isCancel(error)) {
        toast.error("Failed to load subscription list.");
        if (isReset) setListPageData([]);
      }
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  const resetAndFetchTable = () => {
    setListPageData([]);
    setPageApi(1);
    pageRef.current = 1;
    setHasMore(true);
    fetchTablePage(1, true);
  };

  const loadMore = useCallback(() => {
    if (isLoadingMore || !hasMore || isLoading) return;
    const nextPage = pageRef.current + 1;
    pageRef.current = nextPage;
    setPageApi(nextPage);
    fetchTablePage(nextPage);
  }, [isLoadingMore, hasMore, isLoading]);

  const handleScroll = useCallback(
    (e) => {
      if (throttleRef.current || !hasMore || isLoadingMore) return;
      const el = e.target;
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 200) {
        throttleRef.current = true;
        loadMore();
        setTimeout(() => {
          throttleRef.current = false;
        }, 300);
      }
    },
    [loadMore, hasMore, isLoadingMore],
  );

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el || viewMode !== "table") return;
    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
  }, [viewMode, handleScroll]);

  useEffect(() => {
    // When switching to table via "View All", kanbanFilter is already set —
    // listPageData is still populated from the kanban fetch, so no re-fetch needed.
    // Only fetch fresh table data when the user manually switches to table mode
    // (no kanbanFilter active), so existing kanban data is not suitable.
    if (viewMode === "table" && !kanbanFilter) {
      resetAndFetchTable();
    }
  }, [viewMode]);

  // ══════════════════════════════════════════════════════════════════
  // FIX 2: Auto-load more when client-side filtering (kanbanFilter
  // or paymentStatusFilter) reduces visible rows so much that the
  // scroll container doesn't overflow — the scroll event never fires
  // and infinite scroll stalls. This effect detects that situation
  // after every fetch completes and triggers loadMore automatically.
  // ══════════════════════════════════════════════════════════════════
  // ── Apply payment status filter ──
  const paymentFiltered = listPageData;

  // ── Apply kanban column filter on top ──
  const filteredDisplayData = kanbanFilter
    ? paymentFiltered.filter((item) => classifyOrder(item) === kanbanFilter)
    : paymentFiltered;

  useEffect(() => {
    if (viewMode !== "table" || isLoading || isLoadingMore || !hasMore) return;
    const el = scrollContainerRef.current;
    if (!el) return;

    // Wait one frame so the DOM reflects the latest rendered rows
    const rafId = requestAnimationFrame(() => {
      // If the content doesn't fill the scroll container, the user can't
      // scroll, so the scroll-based loadMore will never fire. Trigger it
      // programmatically so we keep fetching until the container overflows
      // or we run out of server data (hasMore becomes false).
      if (el.scrollHeight <= el.clientHeight) {
        loadMore();
      }
    });

    return () => cancelAnimationFrame(rafId);
  }, [
    viewMode,
    isLoading,
    isLoadingMore,
    hasMore,
    loadMore,
    filteredDisplayData.length,
  ]);

  const handleDelete = async (user_id, order_id) => {
    Modal.confirm({
      title: "Delete Subscription",
      icon: <DeleteOutlined style={{ color: "#ff4d4f" }} />,
      content:
        "Are you sure you want to delete this subscription? This action is permanent and cannot be undone.",
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "Cancel",
      centered: true,
      onOk: async () => {
        try {
          setIsLoading(true);
          const response = await deleteOrder({ user_id, order_id });
          if (response.success) {
            toast.success(
              response.message || "Subscription deleted successfully",
            );
            if (viewMode === "kanban") fetchKanban();
            else resetAndFetchTable();
          } else {
            toast.warning(response.message || "Failed to delete subscription.");
          }
        } catch (error) {
          console.error("Delete error:", error);
          toast.error("Failed to delete subscription.");
        } finally {
          setIsLoading(false);
        }
      },
    });
  };

  // ── View All handler from Kanban ──
  const COLUMN_KEY_TO_FILTER = {
    paid: "paid_active",
    free_trial: "trial",
    pending: "payment_pending",
    expiring_soon: "expiring_soon",
    expired: "paid_expired",
    free_trial_expired: "free_trial_expired",
  };

  const handleKanbanViewAll = (columnKey) => {
    setKanbanFilter(null);
    setPaymentStatusFilter(COLUMN_KEY_TO_FILTER[columnKey] || "");
    setViewMode("table");
  };

  const toggleExportMode = () => {
    setExportMode((prev) => !prev);
    setSelectedIds([]);
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredDisplayData.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredDisplayData.map((u) => u.latest_order_id || u.order_id));
    }
  };

  const toggleSelectOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleExportCSV = () => {
    const toExport =
      selectedIds.length > 0
        ? filteredDisplayData.filter((u) => selectedIds.includes(u.latest_order_id || u.order_id))
        : filteredDisplayData;

    const headers = [
      "Customer Name",
      "Email",
      "Mobile",
      "Customer Type",
      "Plan Name",
      "Total Amount",
      "Payment Status",
      "Subscription Start",
      "Subscription End",
      "Days Remaining",
    ];

    const rows = toExport.map((u) => [
      u.customer_name || "",
      u.customer_email || "",
      u.mobile || "",
      u.customer_type || "",
      planMap[u.plan_id] || u.plan_name || "",
      u.latest_order_amount || (u.total_paid_lifetime > 0 ? u.total_paid_lifetime : u.total_amount) || "",
      u.customer_status_label || (u.last_payment_status === "1" || u.last_payment_status === 1 ? "Paid" : "Pending"),
      u.subscription_start_date || "",
      u.subscription_end_date || "",
      u.days_remaining ?? "",
    ]);

    const csvContent = [headers, ...rows]
      .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `subscriptions_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setExportMode(false);
    setSelectedIds([]);
  };

  const activeFiltersCount = [searchName, startDate, endDate, defaultStatusFilter ? "" : paymentStatusFilter, planIdFilter].filter(Boolean).length;

  // ── Get label for active kanban filter ──
  const kanbanFilterLabel = kanbanFilter
    ? COLUMNS.find((c) => c.key === kanbanFilter)?.title || kanbanFilter
    : null;

  const hasClientFilter = !!(kanbanFilter || paymentStatusFilter);
  /* In the board, the columns' own server counts added up — there is no single
     list behind it any more, and `totalUser` would still be reporting whatever
     the table last fetched. */
  const kanbanTotal = Object.values(kanbanCols)
    .reduce((sum, c) => sum + Number(c?.total || 0), 0);
  const displayCount = viewMode === "kanban" ? kanbanTotal : totalUser;

  const labelStyle = {
    fontWeight: 600,
    color: "#374151",
    fontSize: "13px",
    marginBottom: "6px",
    display: "flex",
    alignItems: "center",
    gap: "5px",
  };

  const LoadingMoreRow = () => (
    <div style={{ textAlign: "center", padding: "16px", color: "#9ca3af" }}>
      <div
        className="spinner-border spinner-border-sm"
        style={{ color: "#667eea", marginRight: 8 }}
        role="status"
      />
      <span style={{ fontSize: 13 }}>Loading more...</span>
    </div>
  );

  return (
    <>
      {/* DatePicker Global Styles */}
      <style>{`
        .custom-datepicker-wrapper { width: 100%; }
        .custom-datepicker-wrapper .react-datepicker-wrapper { width: 100%; }
        .custom-datepicker-wrapper .react-datepicker__input-container { width: 100%; }
        .custom-datepicker-wrapper .react-datepicker-popper { z-index: 9999 !important; }
        .custom-datepicker-wrapper .react-datepicker {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          border: 1px solid #e8ecff; border-radius: 12px;
          box-shadow: 0 8px 24px rgba(102, 126, 234, 0.15); overflow: hidden; font-size: 13px;
        }
        .custom-datepicker-wrapper .react-datepicker__month-container { width: 280px; }
        .custom-datepicker-wrapper .react-datepicker__header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-bottom: none; padding: 10px 8px 6px; border-radius: 0;
        }
        .custom-datepicker-wrapper .react-datepicker__current-month { color: white; font-weight: 700; font-size: 14px; margin-bottom: 4px; }
        .custom-datepicker-wrapper .react-datepicker__day-names { display: flex; justify-content: space-around; }
        .custom-datepicker-wrapper .react-datepicker__day-name { color: rgba(255,255,255,0.85); font-weight: 600; font-size: 11px; width: 32px; line-height: 32px; margin: 1px; text-align: center; }
        .custom-datepicker-wrapper .react-datepicker__month { margin: 6px; }
        .custom-datepicker-wrapper .react-datepicker__week { display: flex; justify-content: space-around; }
        .custom-datepicker-wrapper .react-datepicker__day { width: 32px; line-height: 32px; margin: 1px; border-radius: 6px; font-size: 13px; color: #374151; transition: all 0.15s; text-align: center; }
        .custom-datepicker-wrapper .react-datepicker__day:hover { background: #f0f4ff; color: #667eea; }
        .custom-datepicker-wrapper .react-datepicker__day--selected,
        .custom-datepicker-wrapper .react-datepicker__day--keyboard-selected { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important; color: white !important; font-weight: 600; border-radius: 6px; }
        .custom-datepicker-wrapper .react-datepicker__day--today { font-weight: 700; color: #667eea; }
        .custom-datepicker-wrapper .react-datepicker__day--disabled { color: #d1d5db !important; }
        .custom-datepicker-wrapper .react-datepicker__day--outside-month { color: #d1d5db; }
        .custom-datepicker-wrapper .react-datepicker__navigation { top: 10px; }
        .custom-datepicker-wrapper .react-datepicker__navigation-icon::before { border-color: rgba(255,255,255,0.8); border-width: 2px 2px 0 0; height: 7px; width: 7px; }
        .custom-datepicker-wrapper .react-datepicker__navigation:hover .react-datepicker__navigation-icon::before { border-color: white; }
        .custom-datepicker-wrapper .react-datepicker__triangle { display: none; }
        @keyframes meCallModalIn{from{opacity:0;transform:scale(.92) translateY(14px)}to{opacity:1;transform:scale(1) translateY(0)}}
      `}</style>

      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            {/* ── PAGE HEADER ── */}
            <div
              style={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                borderRadius: "16px",
                padding: "28px 32px",
                marginBottom: "28px",
                boxShadow: "0 8px 32px rgba(102,126,234,0.28)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "16px",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "16px" }}
              >
                <div
                  style={{
                    width: "56px",
                    height: "56px",
                    background: "rgba(255,255,255,0.22)",
                    borderRadius: "14px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "26px",
                    color: "white",
                  }}
                >
                  <ShoppingOutlined />
                </div>
                <div>
                  <h4
                    style={{
                      color: "white",
                      margin: 0,
                      fontSize: "24px",
                      fontWeight: 700,
                    }}
                  >
                    Manage Subscription List
                  </h4>
                  <p
                    style={{
                      color: "rgba(255,255,255,0.85)",
                      margin: 0,
                      fontSize: "14px",
                    }}
                  >
                    {filteredUserName
                      ? `Showing subscriptions for ${decodeURIComponent(filteredUserName)}`
                      : "View and manage all subscription records"}
                  </p>
                </div>
              </div>
              <div
                style={{
                  background: "rgba(255,255,255,0.18)",
                  padding: "10px 18px",
                  borderRadius: "10px",
                  display: "flex",
                  gap: "6px",
                  alignItems: "center",
                  fontSize: "13px",
                  color: "white",
                }}
              >
                <a
                  href="javascript:void(0);"
                  style={{ color: "white", textDecoration: "none" }}
                >
                  CMS
                </a>
                <span style={{ color: "rgba(255,255,255,0.5)" }}>›</span>
                <span style={{ fontWeight: 600 }}>
                  Manage Subscription List
                </span>
              </div>
            </div>

            {/* ── FILTER CARD ── */}
            <div style={{ background: "white", borderRadius: "16px", boxShadow: "0 2px 16px rgba(0,0,0,0.07)", border: "1px solid #f0f0f0", marginBottom: "24px", overflow: "visible" }}>
              <div style={{ padding: "16px 24px", display: "flex", alignItems: "center", gap: 12 }}>
                <Input
                  size="large"
                  name="searchName"
                  placeholder="Search by name, email or mobile..."
                  value={searchName}
                  onChange={(e) => {
                    setSearchName(e.target.value);
                    if (!e.target.value) {
                      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
                      setDebouncedSearch("");
                    }
                  }}
                  prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
                  style={{ borderRadius: "8px", flex: 1 }}
                  allowClear
                />
                <Button
                  size="large"
                  icon={<FilterOutlined />}
                  onClick={() => setShowAdvancedFilters(p => !p)}
                  style={{
                    borderRadius: "10px",
                    fontWeight: 600,
                    height: "44px",
                    borderColor: showAdvancedFilters ? "#667eea" : "#e0e0e0",
                    color: showAdvancedFilters ? "#667eea" : "#6b7280",
                    background: showAdvancedFilters ? "#f0f4ff" : "white",
                    display: "flex", alignItems: "center", gap: 6,
                  }}
                >
                  Advanced Filters
                  {activeFiltersCount > 0 && (
                    <span style={{ background: "linear-gradient(135deg,#667eea,#764ba2)", color: "white", borderRadius: 20, padding: "1px 8px", fontSize: 12, fontWeight: 600, marginLeft: 2 }}>
                      {activeFiltersCount}
                    </span>
                  )}
                </Button>
                <Button
                  type="primary"
                  size="large"
                  icon={<SearchOutlined />}
                  onClick={handleSearch}
                  style={{ borderRadius: "10px", fontWeight: 600, height: "44px", background: "linear-gradient(135deg,#667eea 0%,#764ba2 100%)", border: "none", boxShadow: "0 4px 12px rgba(102,126,234,0.4)" }}
                >
                  Search
                </Button>
              </div>

              {showAdvancedFilters && (
                <div style={{ borderTop: "1px solid #f0f0f0", padding: "20px 24px" }}>
                  <div className="row g-3">
                    <div className="col-md-3">
                      <div style={labelStyle}><CalendarOutlined style={{ color: "#667eea" }} /> Subscription Start Date</div>
                      <div className="custom-datepicker-wrapper">
                        <DatePicker selected={startDate} onChange={(date) => setStartDate(date)} dateFormat="dd MMM yyyy" placeholderText="Select start date" maxDate={endDate || undefined} isClearable customInput={<CustomDateInput placeholder="Select start date" />} popperPlacement="bottom-start" popperModifiers={[{ name: "preventOverflow", options: { boundary: "viewport" } }, { name: "offset", options: { offset: [0, 4] } }]} />
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div style={labelStyle}><CalendarOutlined style={{ color: "#667eea" }} /> Subscription End Date</div>
                      <div className="custom-datepicker-wrapper">
                        <DatePicker selected={endDate} onChange={(date) => setEndDate(date)} dateFormat="dd MMM yyyy" placeholderText="Select end date" minDate={startDate} isClearable customInput={<CustomDateInput placeholder="Select end date" />} popperPlacement="bottom-start" popperModifiers={[{ name: "preventOverflow", options: { boundary: "viewport" } }, { name: "offset", options: { offset: [0, 4] } }]} />
                      </div>
                    </div>
                    {!defaultStatusFilter && (
                      <div className="col-md-3">
                        <div style={labelStyle}><FilterOutlined style={{ color: "#667eea" }} /> Customer Status</div>
                        <select value={paymentStatusFilter} onChange={(e) => setPaymentStatusFilter(e.target.value)} style={{ width: "100%", height: "42px", padding: "0 12px", border: "1px solid #e0e0e0", borderRadius: "8px", fontSize: "14px", color: "#374151", background: "white", outline: "none", cursor: "pointer" }}>
                          <option value="">All Statuses</option>
                          <option value="paid_active">Active Paid</option>
                          <option value="trial">Active Free Trial</option>
                          <option value="payment_pending">Payment Pending</option>
                          <option value="paid_expired">Paid Expired</option>
                          <option value="expiring_soon">Expiring Soon</option>
                          <option value="free_trial_expired">Free Trial Expired</option>
                        </select>
                      </div>
                    )}
                    <div className="col-md-3">
                      <div style={labelStyle}><FilterOutlined style={{ color: "#667eea" }} /> Plan</div>
                      <select value={planIdFilter} onChange={(e) => setPlanIdFilter(e.target.value)} style={{ width: "100%", height: "42px", padding: "0 12px", border: "1px solid #e0e0e0", borderRadius: "8px", fontSize: "14px", color: planIdFilter ? "#374151" : "#9ca3af", background: "white", outline: "none", cursor: "pointer" }}>
                        <option value="">All Plans</option>
                        {(allPlansData || []).map((p) => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div style={{ display: "flex", justifyContent: "flex-start", marginTop: "16px" }}>
                    <Button size="large" icon={<ClearOutlined />} onClick={handleClearFilters} disabled={!isFilterApplied} style={{ borderRadius: "10px", fontWeight: 600, borderColor: "#667eea", color: "#667eea", height: "44px" }}>
                      Clear Filters
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* ── TABLE / KANBAN CARD ── */}
            <div
              style={{
                background: "white",
                borderRadius: "16px",
                boxShadow: "0 2px 16px rgba(0,0,0,0.07)",
                border: "1px solid #f0f0f0",
                overflow: "hidden",
              }}
            >
              {/* Header */}
              <div
                style={{
                  padding: "20px 24px",
                  borderBottom: "2px solid #f5f5f5",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "12px",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "12px" }}
                >
                  <div
                    style={{
                      width: "38px",
                      height: "38px",
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      borderRadius: "10px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontSize: "16px",
                    }}
                  >
                    <ShoppingOutlined />
                  </div>
                  <div>
                    <div
                      style={{
                        fontWeight: 700,
                        fontSize: "17px",
                        color: "#1f2937",
                      }}
                    >
                      Subscription List
                    </div>
                    <div style={{ fontSize: "12px", color: "#6b7280" }}>
                      {displayCount} total{" "}
                      {displayCount === 1 ? "record" : "records"}
                    </div>
                  </div>
                  <span
                    style={{
                      background: "#f0f4ff",
                      color: "#667eea",
                      borderRadius: "20px",
                      padding: "3px 12px",
                      fontSize: "13px",
                      fontWeight: 700,
                      marginLeft: "4px",
                    }}
                  >
                    {displayCount}
                  </span>
                </div>

                <div
                  style={{ display: "flex", gap: "10px", alignItems: "center" }}
                >
                  {/* ── Export Controls ── */}
                  {viewMode === "table" && (exportMode ? (
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <Button
                        onClick={toggleSelectAll}
                        style={{
                          height: 36,
                          borderRadius: 8,
                          border: "1px solid #c7d2fe",
                          background: "#f0f4ff",
                          color: "#667eea",
                          fontWeight: 600,
                          fontSize: 13,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 8,
                          paddingInline: 12,
                          boxShadow: "none",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={
                            selectedIds.length === filteredDisplayData.length &&
                            filteredDisplayData.length > 0
                          }
                          onChange={toggleSelectAll}
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            cursor: "pointer",
                            width: 14,
                            height: 14,
                            margin: 0,
                          }}
                        />
                        Select All ({filteredDisplayData.length})
                      </Button>
                      <Button
                        onClick={handleExportCSV}
                        icon={<DownloadOutlined />}
                        style={{
                          height: 36,
                          borderRadius: 8,
                          border: "none",
                          background:
                            "linear-gradient(135deg, #22c55e, #16a34a)",
                          color: "white",
                          fontWeight: 600,
                          fontSize: 13,
                          boxShadow: "0 4px 12px rgba(34,197,94,0.3)",
                          paddingInline: 16,
                        }}
                      >
                        Export{" "}
                        {selectedIds.length > 0
                          ? `(${selectedIds.length})`
                          : `All (${filteredDisplayData.length})`}
                      </Button>
                      <Button
                        onClick={toggleExportMode}
                        style={{
                          height: 36,
                          borderRadius: 8,
                          border: "1px solid #e2e8f0",
                          background: "white",
                          color: "#6b7280",
                          fontWeight: 600,
                          fontSize: 13,
                          paddingInline: 14,
                        }}
                      >
                        ✕ Cancel
                      </Button>
                    </div>
                  ) : (
                    <Button
                      onClick={toggleExportMode}
                      icon={<DownloadOutlined />}
                      style={{
                        height: 36,
                        borderRadius: 8,
                        border: "none",
                        background: "linear-gradient(135deg, #06b6d4, #0891b2)",
                        color: "white",
                        fontWeight: 600,
                        fontSize: 13,
                        paddingInline: 18,
                        boxShadow: "0 4px 12px rgba(8,145,178,0.3)",
                      }}
                    >
                      Export
                    </Button>
                  ))}
                  {/* VIEW MODE TOGGLE */}
                  <div
                    style={{
                      display: "flex",
                      border: "1px solid #e2e8f0",
                      borderRadius: 8,
                      overflow: "hidden",
                    }}
                  >
                    <button
                      onClick={() => {
                        setViewMode("kanban"); setKanbanFilter(null); setExportMode(false); setSelectedIds([]);
                      }}
                      style={{
                        padding: "7px 14px",
                        background:
                          viewMode === "kanban"
                            ? "linear-gradient(135deg,#667eea,#764ba2)"
                            : "white",
                        color: viewMode === "kanban" ? "#fff" : "#6b7280",
                        border: "none",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        fontSize: "13px",
                        fontWeight: 600,
                        transition: "all 0.2s",
                      }}
                    >
                      <AppstoreOutlined /> Kanban
                    </button>
                    <button
                      onClick={() => setViewMode("table")}
                      style={{
                        padding: "7px 14px",
                        background:
                          viewMode === "table"
                            ? "linear-gradient(135deg,#667eea,#764ba2)"
                            : "white",
                        color: viewMode === "table" ? "#fff" : "#6b7280",
                        border: "none",
                        borderLeft: "1px solid #e2e8f0",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        fontSize: "13px",
                        fontWeight: 600,
                        transition: "all 0.2s",
                      }}
                    >
                      <UnorderedListOutlined /> List
                    </button>
                  </div>

                  {filteredUserId && (
                    <button
                      onClick={() =>
                        navigate("/cms/manage-customer/manage-order")
                      }
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                        background: "white",
                        color: "#667eea",
                        borderRadius: "10px",
                        padding: "8px 18px",
                        fontWeight: 600,
                        fontSize: "14px",
                        border: "2px solid #667eea",
                        cursor: "pointer",
                        boxShadow: "0 4px 12px rgba(102,126,234,0.3)",
                      }}
                    >
                      <span
                        style={{
                          width: "22px",
                          height: "22px",
                          background:
                            "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          borderRadius: "6px",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "white",
                          fontSize: "13px",
                        }}
                      >
                        ‹
                      </span>
                      All Subscriptions
                    </button>
                  )}

                  {canCreate && (
                    <Link
                      to="/cms/manage-customer/create-order"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                        background: "linear-gradient(135deg, #52c41a, #389e0d)",
                        color: "white",
                        borderRadius: "10px",
                        padding: "8px 18px",
                        fontWeight: 600,
                        fontSize: "14px",
                        textDecoration: "none",
                        boxShadow: "0 4px 12px rgba(82,196,26,0.3)",
                      }}
                    >
                      <PlusOutlined /> Create Subscription
                    </Link>
                  )}
                </div>
              </div>

              {/* ═══ CONDITIONAL: KANBAN vs TABLE ═══ */}
              {viewMode === "kanban" ? (
                <div style={{ padding: "20px 24px" }}>
                  {/* No screen-wide spinner any more: each column loads on its
                      own and says so in its own header, so gating the whole
                      board on one flag would hide six ready columns while a
                      seventh is still in flight — and `isLoading` starts true
                      and is only cleared by the table's fetch, so the board
                      would have sat on a spinner that never ended. */}
                  <SubscriptionKanban
                    data={filteredDisplayData}
                    onRefresh={fetchKanban}
                    onViewAll={handleKanbanViewAll}
                    planMap={planMap}
                    columnState={kanbanCols}
                    onLoadMoreColumn={loadKanbanColumn}
                  />
                </div>
              ) : (
                /* ═══ TABLE VIEW with infinite scroll ═══ */
                <>
                  {/* Kanban filter banner */}
                  {kanbanFilter && (
                    <div
                      style={{
                        padding: "10px 16px",
                        background: "#f0f4ff",
                        borderBottom: "1px solid #e8ecff",
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <span
                        style={{
                          fontSize: 13,
                          color: "#667eea",
                          fontWeight: 600,
                        }}
                      >
                        Showing: {kanbanFilterLabel} subscriptions
                      </span>
                      <button
                        onClick={() => setKanbanFilter(null)}
                        style={{
                          background: "none",
                          border: "none",
                          color: "#9ca3af",
                          cursor: "pointer",
                          fontSize: 17,
                          lineHeight: 1,
                          padding: "0 4px",
                        }}
                      >
                        ×
                      </button>
                    </div>
                  )}
                  <div
                    ref={scrollContainerRef}
                    style={{
                      maxHeight: "calc(100vh - 340px)",
                      overflowY: "auto",
                    }}
                  >
                    <table
                      style={{ width: "100%", borderCollapse: "collapse" }}
                    >
                      <thead style={{ position: "sticky", top: 0, zIndex: 2 }}>
                        <tr
                          style={{
                            background:
                              "linear-gradient(135deg, #f8f9ff 0%, #f0f4ff 100%)",
                            borderBottom: "2px solid #e8ecff",
                          }}
                        >
                          {exportMode && (
                            <th
                              style={{
                                padding: "14px 16px",
                                width: 40,
                                background: "#f8f9ff",
                                position: "sticky",
                                top: 0,
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={
                                  selectedIds.length ===
                                    filteredDisplayData.length &&
                                  filteredDisplayData.length > 0
                                }
                                onChange={toggleSelectAll}
                                style={{
                                  cursor: "pointer",
                                  width: 15,
                                  height: 15,
                                }}
                              />
                            </th>
                          )}
                          {[
                            "Name",
                            "Email",
                            "Mobile",
                            "Customer Type",
                            "Plan Name",
                            "Plan Price",
                            "Total Amount",
                            "Payment Status",
                            "Subscription Start Date",
                            "Subscription End Date",
                            "Invoice",
                            "Actions",
                          ].map((h) => (
                            <th
                              key={h}
                              style={{
                                padding: "14px 16px",
                                textAlign: "center",
                                fontSize: "12px",
                                fontWeight: 700,
                                color: "#667eea",
                                textTransform: "uppercase",
                                letterSpacing: "0.6px",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {isLoading ? (
                          <TableLoader count={10} />
                        ) : filteredDisplayData?.length > 0 ? (
                          filteredDisplayData.map((item, index) => {
                            // console.log("Rendering item:", item);
                            const isFree =
                              item?.customer_type === "trial" ||
                              item?.plan_type === "Free Trial" ||
                              (item?.payment_link === null &&
                                item?.subscription_type === "free_trial");
                            const isPaid = item?.customer_type === "paid";
                            const isExpired = (() => {
                              if (!item?.subscription_end_date) return false;
                              const [d, m, y] = item.subscription_end_date.split("/");
                              return new Date(`${y}-${m}-${d}`) < new Date();
                            })();
                            const disableEditDelete = isPaid || (!isFree && isExpired);
                            return (
                              <tr
                                key={`${item.latest_order_id || item.order_id}-${index}`}
                                style={{
                                  borderBottom: "1px solid #f5f5f5",
                                  transition: "background 0.15s",
                                  background:
                                    index % 2 === 0 ? "white" : "#fafbff",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = "#f0f4ff";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background =
                                    index % 2 === 0 ? "white" : "#fafbff";
                                }}
                              >
                                {exportMode && (
                                  <td
                                    style={{
                                      padding: "14px 16px",
                                      textAlign: "center",
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={selectedIds.includes(
                                        item.latest_order_id || item.order_id,
                                      )}
                                      onChange={() =>
                                        toggleSelectOne(item.latest_order_id || item.order_id)
                                      }
                                      style={{
                                        cursor: "pointer",
                                        width: 15,
                                        height: 15,
                                      }}
                                    />
                                  </td>
                                )}
                                <td
                                  style={{
                                    padding: "14px 16px",
                                    textAlign: "left",
                                  }}
                                >
                                  <div
                                    onClick={() => navigate(`/cms/manageUserData/user-profile/${item.user_id}`)}
                                    style={{
                                      textDecoration: "none",
                                      cursor: "pointer",
                                    }}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "10px",
                                      }}
                                    >
                                      <div
                                        style={{
                                          width: "34px",
                                          height: "34px",
                                          borderRadius: "50%",
                                          background:
                                            "linear-gradient(135deg, #667eea, #764ba2)",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          color: "white",
                                          fontSize: "13px",
                                          fontWeight: 700,
                                          flexShrink: 0,
                                        }}
                                      >
                                        {item.customer_name
                                          ?.split(" ")
                                          .map((word) => word.charAt(0))
                                          .join("")
                                          .slice(0, 2)
                                          .toUpperCase() || "?"}
                                      </div>
                                      <span
                                        style={{
                                          fontWeight: 600,
                                          color: "#1f2937",
                                          fontSize: "14px",
                                        }}
                                      >
                                        {item.customer_name || "No data"}
                                      </span>
                                    </div>
                                  </div>
                                </td>
                                <td
                                  style={{
                                    padding: "14px 16px",
                                    color: "#4b5563",
                                    fontSize: "13px",
                                    textAlign: "center",
                                  }}
                                >
                                  {item.customer_email || "No data"}
                                </td>
                                <td
                                  style={{
                                    padding: "14px 16px",
                                    fontSize: "13px",
                                    textAlign: "center",
                                  }}
                                >
                                  {item.mobile ? (
                                    <div
                                      style={{
                                        display: "inline-flex",
                                        flexDirection: "column",
                                        alignItems: "flex-start",
                                        gap: "4px",
                                      }}
                                    >
                                      {/* Primary number */}
                                      <div
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: "5px",
                                        }}
                                      >
                                        <div
                                          style={{
                                            width: "6px",
                                            height: "6px",
                                            borderRadius: "50%",
                                            background: "#16a34a",
                                            flexShrink: 0,
                                          }}
                                        />
                                        <span
                                          style={{
                                            fontWeight: 700,
                                            color: "#111827",
                                            fontSize: "13px",
                                            letterSpacing: "0.3px",
                                          }}
                                        >
                                          {item.mobile}
                                        </span>
                                        {item.alternate_number ? (
                                          <Dropdown
                                            trigger={["click"]}
                                            menu={{
                                              items: [
                                                {
                                                  key: "primary",
                                                  label: (
                                                    <div
                                                      style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "8px",
                                                        padding: "2px 0",
                                                      }}
                                                    >
                                                      <div
                                                        style={{
                                                          width: "24px",
                                                          height: "24px",
                                                          borderRadius: "6px",
                                                          background: "#dcfce7",
                                                          display: "flex",
                                                          alignItems: "center",
                                                          justifyContent:
                                                            "center",
                                                        }}
                                                      >
                                                        <PhoneOutlined
                                                          style={{
                                                            color: "#16a34a",
                                                            fontSize: "11px",
                                                          }}
                                                        />
                                                      </div>
                                                      <div>
                                                        <div
                                                          style={{
                                                            fontSize: "11px",
                                                            color: "#6b7280",
                                                            fontWeight: 500,
                                                          }}
                                                        >
                                                          Primary
                                                        </div>
                                                        <div
                                                          style={{
                                                            fontSize: "13px",
                                                            fontWeight: 700,
                                                            color: "#111827",
                                                          }}
                                                        >
                                                          {item.mobile}
                                                        </div>
                                                      </div>
                                                    </div>
                                                  ),
                                                },
                                                {
                                                  key: "alternate",
                                                  label: (
                                                    <div
                                                      style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "8px",
                                                        padding: "2px 0",
                                                      }}
                                                    >
                                                      <div
                                                        style={{
                                                          width: "24px",
                                                          height: "24px",
                                                          borderRadius: "6px",
                                                          background: "#dbeafe",
                                                          display: "flex",
                                                          alignItems: "center",
                                                          justifyContent:
                                                            "center",
                                                        }}
                                                      >
                                                        <PhoneOutlined
                                                          style={{
                                                            color: "#2563eb",
                                                            fontSize: "11px",
                                                          }}
                                                        />
                                                      </div>
                                                      <div>
                                                        <div
                                                          style={{
                                                            fontSize: "11px",
                                                            color: "#6b7280",
                                                            fontWeight: 500,
                                                          }}
                                                        >
                                                          Alternate
                                                        </div>
                                                        <div
                                                          style={{
                                                            fontSize: "13px",
                                                            fontWeight: 700,
                                                            color: "#111827",
                                                          }}
                                                        >
                                                          {
                                                            item.alternate_number
                                                          }
                                                        </div>
                                                      </div>
                                                    </div>
                                                  ),
                                                },
                                              ],
                                              onClick: ({ key }) => {
                                                const num =
                                                  key === "primary"
                                                    ? item.mobile
                                                    : item.alternate_number;
                                                handleClickToCall(
                                                  num,
                                                  item.customer_name,
                                                );
                                                setCallOutcome("Connected");
                                                setCallNote("");
                                                setCallDurationME("");
                                                setCallFuStatusME("");
                                                setCallNextDateME("");
                                                setCallNextTimeME("");
                                                setCallLogModal({
                                                  phone: num,
                                                  name: item.customer_name,
                                                  id: item.user_id,
                                                });
                                              },
                                            }}
                                          >
                                            <button
                                              style={{
                                                width: "18px",
                                                height: "18px",
                                                borderRadius: "5px",
                                                background: "#dcfce7",
                                                border: "none",
                                                color: "#16a34a",
                                                cursor: "pointer",
                                                display: "inline-flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                padding: 0,
                                                fontSize: "11px",
                                                flexShrink: 0,
                                              }}
                                            >
                                              <PhoneOutlined />
                                            </button>
                                          </Dropdown>
                                        ) : (
                                          <Tooltip title="Log a Call">
                                            <button
                                              onClick={() => {
                                                handleClickToCall(
                                                  item.mobile,
                                                  item.customer_name,
                                                );
                                                setCallOutcome("Connected");
                                                setCallNote("");
                                                setCallDurationME("");
                                                setCallFuStatusME("");
                                                setCallNextDateME("");
                                                setCallNextTimeME("");
                                                setCallLogModal({
                                                  phone: item.mobile,
                                                  name: item.customer_name,
                                                  id: item.user_id,
                                                });
                                              }}
                                              style={{
                                                width: "18px",
                                                height: "18px",
                                                borderRadius: "5px",
                                                background: "#dcfce7",
                                                border: "none",
                                                color: "#16a34a",
                                                cursor: "pointer",
                                                display: "inline-flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                padding: 0,
                                                fontSize: "11px",
                                                flexShrink: 0,
                                              }}
                                            >
                                              <PhoneOutlined />
                                            </button>
                                          </Tooltip>
                                        )}
                                      </div>
                                      {/* Alternate number */}
                                      {item.alternate_number && (
                                        <div
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "5px",
                                          }}
                                        >
                                          <div
                                            style={{
                                              width: "6px",
                                              height: "6px",
                                              borderRadius: "50%",
                                              background: "#2563eb",
                                              flexShrink: 0,
                                            }}
                                          />
                                          <span
                                            style={{
                                              fontWeight: 600,
                                              color: "#374151",
                                              fontSize: "12px",
                                              letterSpacing: "0.3px",
                                            }}
                                          >
                                            {item.alternate_number}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <span
                                      style={{
                                        color: "#9ca3af",
                                        fontStyle: "italic",
                                      }}
                                    >
                                      No data
                                    </span>
                                  )}
                                </td>
                                <td
                                  style={{
                                    padding: "14px 16px",
                                    textAlign: "center",
                                  }}
                                >
                                  <span
                                    style={{
                                      background: "#fff7e6",
                                      color: "#d97706",
                                      borderRadius: "6px",
                                      padding: "3px 10px",
                                      fontSize: "12px",
                                      fontWeight: 600,
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {item.customer_type === "trial" ? "Free Trial" : item.customer_type === "paid" ? "Paid" : item.customer_type || (item.is_attorney === 1 ? "Attorney/IP" : "Corporate")}
                                  </span>
                                </td>
                                <td
                                  style={{
                                    padding: "14px 16px",
                                    textAlign: "center",
                                  }}
                                >
                                  <span
                                    style={{
                                      background: "#f0f4ff",
                                      color: "#667eea",
                                      borderRadius: "6px",
                                      padding: "3px 10px",
                                      fontSize: "12px",
                                      fontWeight: 600,
                                      whiteSpace: "nowrap",
                                      display: "inline-block",
                                    }}
                                  >
                                    {planMap[item.plan_id] ||
                                      item.plan_name ||
                                      "No data"}
                                  </span>
                                </td>
                                <td
                                  style={{
                                    padding: "14px 16px",
                                    color: "#4b5563",
                                    fontSize: "13px",
                                    textAlign: "center",
                                    fontWeight: 600,
                                  }}
                                >
                                  ₹{item.plan_price || 0}
                                </td>
                                <td
                                  style={{
                                    padding: "14px 16px",
                                    color: "#4b5563",
                                    fontSize: "13px",
                                    textAlign: "center",
                                    fontWeight: 600,
                                  }}
                                >
                                  ₹{item.latest_order_amount || (item.total_paid_lifetime > 0 ? item.total_paid_lifetime : item.total_amount) || 0}
                                </td>
                                <td
                                  style={{
                                    padding: "14px 16px",
                                    textAlign: "center",
                                  }}
                                >
                                  {isFree ? (
                                    <span
                                      style={{
                                        background: "#e0f2fe",
                                        color: "#0369a1",
                                        border: "1px solid #bae6fd",
                                        borderRadius: "20px",
                                        padding: "3px 12px",
                                        fontSize: "12px",
                                        fontWeight: 600,
                                      }}
                                    >
                                      ● Free Trial
                                    </span>
                                  ) : (item?.last_payment_status === "0" || item?.last_payment_status === 0 || item?.payment_status === "0" || item?.customer_type === "pending") ? (
                                    <span
                                      style={{
                                        background: "#fef2f2",
                                        color: "#dc2626",
                                        border: "1px solid #fecaca",
                                        borderRadius: "20px",
                                        padding: "3px 12px",
                                        fontSize: "12px",
                                        fontWeight: 600,
                                      }}
                                    >
                                      ● Pending
                                    </span>
                                  ) : (
                                    <span
                                      style={{
                                        background: "#f0fdf4",
                                        color: "#16a34a",
                                        border: "1px solid #bbf7d0",
                                        borderRadius: "20px",
                                        padding: "3px 12px",
                                        fontSize: "12px",
                                        fontWeight: 600,
                                      }}
                                    >
                                      ● Paid
                                    </span>
                                  )}
                                </td>
                                <td
                                  style={{
                                    padding: "14px 16px",
                                    textAlign: "center",
                                    color: "#4b5563",
                                    fontSize: "13px",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {item.subscription_start_date ? (
                                    (() => {
                                      const [d, m, y] =
                                        item.subscription_start_date.split("/");
                                      return (
                                        <span
                                          style={{
                                            color: "#4f46e5",
                                            fontSize: "13px",
                                            fontWeight: 600,
                                          }}
                                        >
                                          {new Date(
                                            `${y}-${m}-${d}`,
                                          ).toLocaleDateString("en-GB", {
                                            day: "2-digit",
                                            month: "short",
                                            year: "numeric",
                                          })}
                                        </span>
                                      );
                                    })()
                                  ) : (
                                    <span
                                      style={{
                                        color: "#9ca3af",
                                        fontStyle: "italic",
                                        fontSize: "12px",
                                      }}
                                    >
                                      N/A
                                    </span>
                                  )}
                                </td>
                                <td
                                  style={{
                                    padding: "14px 16px",
                                    textAlign: "center",
                                    color: "#4b5563",
                                    fontSize: "13px",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {item.subscription_end_date ? (
                                    (() => {
                                      const [d, m, y] =
                                        item.subscription_end_date.split("/");
                                      return (
                                        <span
                                          style={{
                                            color: "#d97706",
                                            fontSize: "13px",
                                            fontWeight: 600,
                                          }}
                                        >
                                          {new Date(
                                            `${y}-${m}-${d}`,
                                          ).toLocaleDateString("en-GB", {
                                            day: "2-digit",
                                            month: "short",
                                            year: "numeric",
                                          })}
                                        </span>
                                      );
                                    })()
                                  ) : (
                                    <span
                                      style={{
                                        color: "#9ca3af",
                                        fontStyle: "italic",
                                        fontSize: "12px",
                                      }}
                                    >
                                      N/A
                                    </span>
                                  )}
                                </td>
                                <td
                                  style={{
                                    padding: "14px 16px",
                                    textAlign: "center",
                                  }}
                                >
                                  {item.invoice_url ? (
                                    <div
                                      style={{
                                        display: "flex",
                                        gap: "6px",
                                        alignItems: "center",
                                        justifyContent: "center",
                                      }}
                                    >
                                      <Tooltip
                                        title={
                                          (item.last_payment_status === "0" || item.last_payment_status === 0 || item.payment_status === "0")
                                            ? "Invoice not available (unpaid)"
                                            : "Download Invoice"
                                        }
                                      >
                                        {(item.last_payment_status === "0" || item.last_payment_status === 0 || item.payment_status === "0") ? (
                                          <span
                                            style={{
                                              width: "32px",
                                              height: "32px",
                                              display: "inline-flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              background: "#f5f5f5",
                                              color: "#d1d5db",
                                              borderRadius: "8px",
                                              border: "1px solid #e5e7eb",
                                              fontSize: "13px",
                                              cursor: "not-allowed",
                                            }}
                                          >
                                            <DownloadOutlined />
                                          </span>
                                        ) : (
                                          <a
                                            href={item.invoice_url}
                                            download={`Invoice_Order_${item.latest_order_id || item.order_id}.pdf`}
                                            style={{
                                              width: "32px",
                                              height: "32px",
                                              display: "inline-flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              background: "#f0fdf4",
                                              color: "#16a34a",
                                              borderRadius: "8px",
                                              border: "1px solid #bbf7d0",
                                              textDecoration: "none",
                                              fontSize: "13px",
                                            }}
                                          >
                                            <DownloadOutlined />
                                          </a>
                                        )}
                                      </Tooltip>
                                    </div>
                                  ) : (
                                    <span
                                      style={{
                                        color: "#9ca3af",
                                        fontStyle: "italic",
                                        fontSize: "12px",
                                      }}
                                    >
                                      N/A
                                    </span>
                                  )}
                                </td>
                                <td
                                  style={{
                                    padding: "14px 16px",
                                    textAlign: "center",
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      gap: "6px",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    {(item?.last_payment_status !== "1" && item?.last_payment_status !== 1 && item?.payment_status !== "1") && (
                                      <Tooltip title="Communications">
                                        <button
                                          onClick={() =>
                                            navigate(`/cms/inbox?userId=${item.user_id}`)
                                          }
                                          style={{
                                            width: "32px",
                                            height: "32px",
                                            display: "inline-flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            background: "#f0f4ff",
                                            color: "#4f46e5",
                                            borderRadius: "8px",
                                            border: "1px solid #c7d2fe",
                                            cursor: "pointer",
                                            fontSize: "13px",
                                          }}
                                        >
                                          <CommentOutlined />
                                        </button>
                                      </Tooltip>
                                    )}
                                    {canEdit &&
                                      (disableEditDelete ? (
                                        <Tooltip title="Upgrade subscription from profile">
                                          <button
                                            disabled
                                            style={{
                                              width: "32px",
                                              height: "32px",
                                              display: "inline-flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              background: "#f5f5f5",
                                              color: "#bfbfbf",
                                              borderRadius: "8px",
                                              border: "1px solid #e5e5e5",
                                              cursor: "not-allowed",
                                              fontSize: "13px",
                                            }}
                                          >
                                            <EditOutlined />
                                          </button>
                                        </Tooltip>
                                      ) : (
                                        <Tooltip title="Upgrade Subscription">
                                          <Link
                                            to={`/cms/manage-customer/edit-order/${item.latest_order_id || item.order_id}/${item.user_id}`}
                                            style={{
                                              width: "32px",
                                              height: "32px",
                                              display: "inline-flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              background: "#eff6ff",
                                              color: "#2563eb",
                                              borderRadius: "8px",
                                              border: "1px solid #bfdbfe",
                                              textDecoration: "none",
                                              fontSize: "13px",
                                            }}
                                          >
                                            <EditOutlined />
                                          </Link>
                                        </Tooltip>
                                      ))}
                                    {canDelete && (
                                      <Tooltip title={disableEditDelete ? "Cannot delete active or expired paid subscription" : "Delete"}>
                                        <button
                                          disabled={disableEditDelete}
                                          onClick={() =>
                                            !disableEditDelete && handleDelete(
                                              item.user_id,
                                              item.latest_order_id || item.order_id,
                                            )
                                          }
                                          style={{
                                            width: "32px",
                                            height: "32px",
                                            display: "inline-flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            background: disableEditDelete ? "#f5f5f5" : "#fef2f2",
                                            color: disableEditDelete ? "#bfbfbf" : "#dc2626",
                                            borderRadius: "8px",
                                            border: `1px solid ${disableEditDelete ? "#e5e5e5" : "#fecaca"}`,
                                            cursor: disableEditDelete ? "not-allowed" : "pointer",
                                            fontSize: "13px",
                                          }}
                                        >
                                          <DeleteOutlined />
                                        </button>
                                      </Tooltip>
                                    )}
                                    {isFree || item?.last_payment_status === "1" || item?.last_payment_status === 1 || item?.payment_status === "1" ? (
                                      <Tooltip
                                        title={
                                          isFree
                                            ? "Not available for Free Trial"
                                            : "Already paid"
                                        }
                                      >
                                        <button
                                          disabled
                                          style={{
                                            width: "32px",
                                            height: "32px",
                                            display: "inline-flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            background: "#f5f5f5",
                                            color: "#bfbfbf",
                                            borderRadius: "8px",
                                            border: "1px solid #e5e5e5",
                                            cursor: "not-allowed",
                                            fontSize: "13px",
                                          }}
                                        >
                                          <LinkOutlined />
                                        </button>
                                      </Tooltip>
                                    ) : (
                                      <Tooltip title="View / Share Payment Link">
                                        <button
                                          onClick={() => openPaymentModal(item)}
                                          style={{
                                            width: "32px",
                                            height: "32px",
                                            display: "inline-flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            background: "#f0fdf4",
                                            color: "#16a34a",
                                            borderRadius: "8px",
                                            border: "1px solid #bbf7d0",
                                            cursor: "pointer",
                                            fontSize: "13px",
                                          }}
                                        >
                                          <LinkOutlined />
                                        </button>
                                      </Tooltip>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td
                              colSpan="9"
                              style={{
                                padding: "60px 20px",
                                textAlign: "center",
                              }}
                            >
                              <div style={{ color: "#9ca3af" }}>
                                <ShoppingOutlined
                                  style={{
                                    fontSize: "48px",
                                    marginBottom: "12px",
                                    display: "block",
                                  }}
                                />
                                <div
                                  style={{
                                    fontSize: "16px",
                                    fontWeight: 600,
                                    color: "#6b7280",
                                  }}
                                >
                                  No Data Found
                                </div>
                                <div
                                  style={{
                                    fontSize: "13px",
                                    color: "#9ca3af",
                                    marginTop: "4px",
                                  }}
                                >
                                  Try adjusting your filters
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                    {isLoadingMore && <LoadingMoreRow />}
                    {!hasMore && listPageData.length > 0 && !isLoading && (
                      <div
                        style={{
                          textAlign: "center",
                          padding: "14px",
                          fontSize: 12,
                          color: "#9ca3af",
                        }}
                      >
                        All {displayCount} records loaded
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Call Log Modal ── */}
      {callLogModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1100,
            background: "rgba(15,23,42,.45)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: "15px",
              width: "100%",
              maxWidth: "600px",
              maxHeight: "90vh",
              border: "2px solid #fde68a",
              overflow: "hidden",
              boxShadow: "0 5px 24px rgba(245,158,11,.1)",
              animation: "meCallModalIn .25s ease",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                background: "linear-gradient(135deg,#f59e0b,#d97706)",
                padding: "11px 18px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexShrink: 0,
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "9px" }}
              >
                <div
                  style={{
                    width: "30px",
                    height: "30px",
                    background: "rgba(255,255,255,.2)",
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                  }}
                >
                  <PhoneOutlined />
                </div>
                <div>
                  <span
                    style={{
                      color: "white",
                      fontWeight: 700,
                      fontSize: "14px",
                    }}
                  >
                    Log a Call
                  </span>
                  <div
                    style={{
                      color: "rgba(255,255,255,.75)",
                      fontSize: "11px",
                      marginTop: "1px",
                    }}
                  >
                    {callLogModal.name} ({callLogModal.phone})
                  </div>
                </div>
              </div>
              <button
                onClick={resetCallLog}
                style={{
                  width: "26px",
                  height: "26px",
                  borderRadius: "50%",
                  background: "rgba(255,255,255,.2)",
                  border: "none",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
              >
                ✕
              </button>
            </div>
            <div
              style={{
                padding: "16px 18px",
                flex: 1,
                overflowY: "auto",
                minHeight: 0,
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "11px",
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "11px",
                  }}
                >
                  <div>
                    <label
                      style={{
                        fontSize: "11px",
                        fontWeight: 700,
                        color: "#6b7280",
                        display: "block",
                        marginBottom: "5px",
                        textTransform: "uppercase",
                        letterSpacing: "0.4px",
                      }}
                    >
                      Call Outcome
                    </label>
                    <select
                      value={callOutcome}
                      onChange={(e) => setCallOutcome(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "9px 12px",
                        border: "1.5px solid #fde68a",
                        borderRadius: "9px",
                        fontSize: "13px",
                        outline: "none",
                        background: "white",
                        cursor: "pointer",
                        boxSizing: "border-box",
                      }}
                    >
                      {[
                        "Connected",
                        "No Answer",
                        "Voicemail Left",
                        "Call Back Later",
                        "Wrong Number",
                      ].map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label
                      style={{
                        fontSize: "11px",
                        fontWeight: 700,
                        color: "#6b7280",
                        display: "block",
                        marginBottom: "5px",
                        textTransform: "uppercase",
                        letterSpacing: "0.4px",
                      }}
                    >
                      Call Duration
                    </label>
                    <input
                      value={callDurationME}
                      onChange={(e) => setCallDurationME(e.target.value)}
                      placeholder="e.g. 15 mins"
                      style={{
                        width: "100%",
                        padding: "9px 12px",
                        border: "1.5px solid #fde68a",
                        borderRadius: "9px",
                        fontSize: "13px",
                        outline: "none",
                        background: "white",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>
                </div>
                <div>
                  <label
                    style={{
                      fontSize: "11px",
                      fontWeight: 700,
                      color: "#6b7280",
                      display: "block",
                      marginBottom: "5px",
                      textTransform: "uppercase",
                      letterSpacing: "0.4px",
                    }}
                  >
                    Call Notes
                  </label>
                  <textarea
                    placeholder="Add notes..."
                    value={callNote}
                    onChange={(e) => setCallNote(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      border: "1.5px solid #fde68a",
                      borderRadius: "9px",
                      fontSize: "13px",
                      outline: "none",
                      resize: "vertical",
                      minHeight: "80px",
                      background: "white",
                      fontFamily: "inherit",
                      boxSizing: "border-box",
                      lineHeight: 1.6,
                    }}
                  />
                </div>
                <div>
                  <label
                    style={{
                      fontSize: "11px",
                      fontWeight: 700,
                      color: "#6b7280",
                      display: "block",
                      marginBottom: "5px",
                      textTransform: "uppercase",
                      letterSpacing: "0.4px",
                    }}
                  >
                    Follow-up Status
                  </label>
                  <FollowUpStatusSelectME
                    value={callFuStatusME}
                    onChange={setCallFuStatusME}
                    followUpOptionsList={followUpOptions}
                  />
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "11px",
                    marginTop: "4px",
                    paddingTop: "10px",
                    borderTop: "1px dashed #fde68a",
                  }}
                >
                  <div>
                    <label
                      style={{
                        fontSize: "10px",
                        fontWeight: 700,
                        color: "#9ca3af",
                        display: "flex",
                        alignItems: "center",
                        gap: "3px",
                        marginBottom: "3px",
                        textTransform: "uppercase",
                        letterSpacing: "0.4px",
                      }}
                    >
                      <CalendarOutlined style={{ fontSize: "11px" }} /> Next
                      Follow-up
                    </label>
                    <input
                      type="date"
                      value={callNextDateME}
                      onChange={(e) => setCallNextDateME(e.target.value)}
                      onClick={(e) => e.target.showPicker?.()}
                      style={{
                        width: "100%",
                        padding: "7px 10px",
                        border: "1.5px solid #e2e8f0",
                        borderRadius: "7px",
                        fontSize: "12px",
                        outline: "none",
                        background: "white",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        fontSize: "10px",
                        fontWeight: 700,
                        color: "#9ca3af",
                        display: "flex",
                        alignItems: "center",
                        gap: "3px",
                        marginBottom: "3px",
                        textTransform: "uppercase",
                        letterSpacing: "0.4px",
                      }}
                    >
                      Time
                    </label>
                    <input
                      type="time"
                      value={callNextTimeME}
                      onChange={(e) => setCallNextTimeME(e.target.value)}
                      onClick={(e) => e.target.showPicker?.()}
                      style={{
                        width: "100%",
                        padding: "7px 10px",
                        border: "1.5px solid #e2e8f0",
                        borderRadius: "7px",
                        fontSize: "12px",
                        outline: "none",
                        background: "white",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "8px",
                    marginTop: "6px",
                  }}
                >
                  <button
                    onClick={resetCallLog}
                    style={{
                      padding: "8px 18px",
                      background: "#f1f5f9",
                      color: "#6b7280",
                      border: "1.5px solid #e2e8f0",
                      borderRadius: "10px",
                      fontSize: "13px",
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    disabled={addCustomerFollowUp.isPending}
                    onClick={handleLogCallSubmit}
                    style={{
                      padding: "8px 18px",
                      background: "linear-gradient(135deg,#f59e0b,#d97706)",
                      color: "white",
                      border: "none",
                      borderRadius: "10px",
                      fontSize: "13px",
                      fontWeight: 600,
                      cursor: addCustomerFollowUp.isPending
                        ? "not-allowed"
                        : "pointer",
                      boxShadow: "0 4px 12px rgba(245,158,11,.3)",
                      opacity: addCustomerFollowUp.isPending ? 0.7 : 1,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "7px",
                    }}
                  >
                    {addCustomerFollowUp.isPending ? (
                      <span className="spinner-border spinner-border-sm text-white" />
                    ) : (
                      <>
                        <CheckCircleOutlined style={{ fontSize: "12px" }} /> Log
                        Call
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Payment Modal ── */}
      {showPaymentModal && selectedOrder && (
        <Modal
          open={showPaymentModal}
          onCancel={closePaymentModal}
          footer={null}
          centered
          width={720}
          destroyOnClose
        >
          <div className="mb-4">
            <h5 className="fw-bold mb-1 d-flex align-items-center">
              <i className="fa fa-credit-card me-2 text-success"></i>
              Payment Details
            </h5>
            <span className="text-muted" style={{ fontSize: 12 }}>
              Share the payment link with the customer
            </span>
          </div>
          <div className="row mb-4">
            <div className="col-md-6">
              <div className="text-muted" style={{ fontSize: 12 }}>
                Order ID
              </div>
              <div className="fw-semibold fs-6">#{selectedOrder?.order_id}</div>
            </div>
            <div className="col-md-6 text-md-end">
              <div className="text-muted" style={{ fontSize: 12 }}>
                Created At
              </div>
              <div className="fw-semibold fs-6">
                {new Date(selectedOrder?.order_created_at).toLocaleString(
                  "en-IN",
                )}
              </div>
            </div>
          </div>
          <div className="mb-4">
            <div className="fw-semibold mb-1">Payment Link</div>
            <Input
              value={selectedOrder?.payment_link}
              readOnly
              size="large"
              addonAfter={
                linkCopied ? (
                  <CheckOutlined style={{ color: "#52c41a", fontSize: 16 }} />
                ) : (
                  <CopyOutlined
                    style={{ cursor: "pointer", fontSize: 16 }}
                    onClick={copyPaymentLink}
                  />
                )
              }
            />
            <small className="text-muted">
              Share this link with the customer to complete payment
            </small>
          </div>
          <div>
            <div className="fw-semibold mb-2">Send Payment Link</div>
            <div className="d-flex gap-3 flex-wrap">
              <Button
                size="large"
                style={{ borderColor: "#1677ff", color: "#1677ff" }}
                icon={<MailOutlined />}
                onClick={() =>
                  window.open(
                    `mailto:${selectedOrder.customer_email}?subject=Payment Link for Order ${selectedOrder.order_id}&body=${selectedOrder.payment_link}`,
                  )
                }
              >
                Email
              </Button>
              <Button
                size="large"
                style={{ borderColor: "#25D366", color: "#25D366" }}
                icon={<WhatsAppOutlined />}
                onClick={() =>
                  window.open(
                    `https://wa.me/91${selectedOrder.mobile}?text=${encodeURIComponent(`Payment link for Order #${selectedOrder.order_id}: ${selectedOrder.payment_link}`)}`,
                  )
                }
              >
                WhatsApp
              </Button>
              <Button
                size="large"
                style={{ borderColor: "#6c757d", color: "#6c757d" }}
                icon={<MessageOutlined />}
                onClick={() =>
                  window.open(
                    `sms:${selectedOrder.mobile}?body=${encodeURIComponent(`Payment link for Order #${selectedOrder.order_id}: ${selectedOrder.payment_link}`)}`,
                  )
                }
              >
                SMS
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default ManageOrderList;
